import { RootState } from "@/redux/store";
import {
  useAddApproveDocMutation,
  useAddAttachmentWithEntityMutation,
  useAddLineItemCommentMutation,
  useAddParkDocMutation,
  useAddRejectDocMutation,
  useDeleteAttachmentMutation,
  useDeleteLineItemByIdMutation,
  useGetAttachmentWithEntity,
  useGetCommentByLineItemId,
  useGetDocumentTypeDropdown,
  useGetGlMasterDataDropdown,
  useGetParkedDocById,
  useGetPaymentMethodByCommunity,
  useGetPaymentTermsByCommunity,
  useGetPostedDocById,
  useGetVendorListByCommunityId,
  usePostDocMutation,
  useSubmitForApprovalDocMutation,
  useUpdateParkDocMutation,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "@/components/Toast";
import { ATTACHMENT, DOCUMENT_ID, LINE_ITEMS, PARK_ITEM_ID } from "./consts";
import { useEffect, useState } from "react";
import { useWatch } from "react-hook-form";

const useApi = ({ form, onBackClick, currentAction, formReset }: any) => {
  const { invoiceId, invoiceAction } = useParams();
  const isPosted = invoiceAction == "posted";
  const [isEntryView, setIsEntryView] = useState<boolean>(true);
  const queryClient = useQueryClient();
  const [lineItemComment, setLineItemComment] = useState<string>("");
  const [lineItem, setLineItem] = useState<any>(null);
  const [openApproveRejectDialog, setOpenApproveRejectDialog] =
    useState<boolean>(false);
  const [openPostDialog, setOpenPostDialog] = useState<boolean>(false);
  const [deletedLineItem, setDeletedLineItem] = useState<any>("");
  const [openDeleteLineItemDialog, setOpenDeleteLineItemDialog] =
    useState<boolean>(false);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const allInvoiceStatus = useSelector(
    (state: RootState) => state.currentData.allInvoiceStatus
  );

  const allPost =
    isPosted || (invoiceAction == "all" && allInvoiceStatus == "posted");

  const { data: vendorList, isLoading: isVendorLoading } =
    useGetVendorListByCommunityId({
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: glAccount, isLoading: isGLAccountLoading } =
    useGetGlMasterDataDropdown({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: documentTypes, isLoading: isDocumentTypeLoading } =
    useGetDocumentTypeDropdown({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: paymentMethodOptions, isLoading: isPaymentMethodLoading } =
    useGetPaymentMethodByCommunity({communityId: communityDetail?.communityId});

  const { data: paymentTermOptions, isLoading: isPaymentTermLoading } =
    useGetPaymentTermsByCommunity({communityId: communityDetail?.communityId});

  const {
    mutate: addSubmitForApprovalInfo,
    isPending: isAddSubmitForApprovalPending,
  } = useSubmitForApprovalDocMutation({
    onError: (error?: any) => {
      queryClient.invalidateQueries({
        queryKey: ["park-documents"],
      });
      onBackClick();
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: ({data}: any) => {
      queryClient.invalidateQueries({
        queryKey: ["park-documents"],
      });
      onBackClick();
      toast.success(`Document ${data?.data?.parkDocumentHeaderId} Submitted for Approval Successfully`);
    },
  });

  const { mutateAsync: addComment, isPending: isAddCommentPending } =
    useAddLineItemCommentMutation();

  const { mutateAsync: attachmentInfo, isPending: isAttachmentPending } =
    useAddAttachmentWithEntityMutation();

  const { mutateAsync: deleteAttachmentInfo, isPending: isDeletePending } =
    useDeleteAttachmentMutation();

  const { mutate: addInfo, isPending: isAddPending } = useAddParkDocMutation({
    onError: (error?: any) => {
      if (error?.response?.data?.message?.toLowerCase()?.includes("validation")) {
        const msg = error?.response?.data?.data?.map((err: any) => `Row No: ${err?.rowNumber} - ${err?.errorMessage}`)
        toast.error(msg?.join(", ")
        );
      } else {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
      }
      return error?.response?.data?.message;
    },
    onSuccess: async (data: any) => {
      try {
        if (currentAction == 2) {
          addSubmitForApprovalInfo({
            communityId: communityDetail?.communityId as string,
            parkDocId: data?.data?.data?.header?.[DOCUMENT_ID],
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: ["park-documents"],
          });

          // ✅ Final toast after everything
          toast.success(`Document (ID: ${data?.data?.data?.header?.[DOCUMENT_ID]}) Added Successfully`);
        }

        // Collect all promises
        const additionalPromises: Promise<any>[] = [];

        if (lineItemComment) {
          const lineItemIds =
            data?.data?.data?.[LINE_ITEMS]?.map(
              (data: any) => data?.[PARK_ITEM_ID]
            ) || [];
          additionalPromises.push(
            addComment({
              data: {
                comment: lineItemComment,
                lineItemIds,
              },
            })
          );
        }

        if (form.watch(ATTACHMENT)) {
          additionalPromises.push(
            attachmentInfo({
              entityId: data?.data?.data?.header?.[DOCUMENT_ID],
              entityType: "INVOICE",
              data: {
                file: form.watch(ATTACHMENT),
              },
            })
          );
        }

        // Wait for all async actions to complete
        await Promise.all(additionalPromises);
        formReset();
      } catch (error) {
        console.error("Error in post-success APIs:", error);
        toast.error("Something went wrong while processing additional data.", {
          className: "!bg-red-600 !text-white",
        });
      }
    },
  });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateParkDocMutation({
      onError: (error?: any) => {
        if (error?.response?.data?.message?.toLowerCase()?.includes("validation")) {
          const msg = error?.response?.data?.data?.map((err: any) => `Row No: ${err?.rowNumber} - ${err?.errorMessage}`)
          toast.error(msg?.join(", ")
          );
        } else {
          toast.error(
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong"
          );
        }
        return error?.response?.data?.message;
      },
      onSuccess: async (data: any) => {
        try {
          if (currentAction == 2) {
            addSubmitForApprovalInfo({
              communityId: communityDetail?.communityId as string,
              parkDocId: data?.data?.data?.header?.[DOCUMENT_ID],
            });
          } else {
            queryClient.invalidateQueries({
              queryKey: ["park-documents"],
            });

            toast.success(`Document ${invoiceId} Updated Successfully`);
          }

          // Collect all promises
          const additionalPromises: Promise<any>[] = [];

          if (lineItemComment) {
            const lineItemIds =
              data?.data?.data?.[LINE_ITEMS]?.map(
                (data: any) => data?.[PARK_ITEM_ID]
              ) || [];
            additionalPromises.push(
              addComment({
                data: {
                  comment: lineItemComment,
                  lineItemIds,
                },
              })
            );
          }

          if (!form.watch(ATTACHMENT)?.attachmentId) {
            if (
              attachment?.data?.[attachment?.data?.length - 1]?.attachmentId
            ) {
              additionalPromises.push(
                deleteAttachmentInfo({
                  attachmentId:
                    attachment?.data?.[attachment?.data?.length - 1]
                      ?.attachmentId,
                })
              );
            }

            if (form.watch(ATTACHMENT)) {
              additionalPromises.push(
                attachmentInfo({
                  entityId: data?.data?.data?.header?.[DOCUMENT_ID],
                  entityType: "INVOICE",
                  data: {
                    file: form.watch(ATTACHMENT),
                  },
                })
              );
            }
          }

          onBackClick();

          // Wait for all async actions to complete
          await Promise.all(additionalPromises);
        } catch (error) {
          console.error("Error in post-success APIs:", error);
          toast.error(
            "Something went wrong while processing additional data.",
            {
              className: "!bg-red-600 !text-white",
            }
          );
        }
      },
    });

  const { data: parkDocDetails, isLoading: isParkDetailLoading } =
    useGetParkedDocById({
      communityId: communityDetail?.communityId as string,
      id: invoiceId as string,
      queryParams: {
        enabled: !!(invoiceId && !allPost),
      },
    });

  const { data: attachment, isLoading: isAttachmentLoading } =
    useGetAttachmentWithEntity({
      entityId: invoiceId as string,
      entityType: "INVOICE",
      queryParams: {
        enabled: !!invoiceId,
      },
    });

  const { data: postedDocDetails, isLoading: isPostedDetailLoading, refetch: refetchPostedDoc } =
    useGetPostedDocById({
      communityId: communityDetail?.communityId as string,
      id: invoiceId as string,
      entryView: isEntryView,
      queryParams: {
        enabled: !!(invoiceId && allPost),
      },
    });

  useEffect(() => {
    if (invoiceId && allPost) {
      refetchPostedDoc();
    }
  }, [isEntryView]);

  const docDetails = allPost ? postedDocDetails : parkDocDetails;
  const isDetailLoading = allPost ? isParkDetailLoading : isPostedDetailLoading;
  const formLineItems = useWatch({control: form.control, name: LINE_ITEMS});

  const { data: lineItemCommentText, isLoading: isCommentLoading } =
    useGetCommentByLineItemId({
      id: lineItem?.[PARK_ITEM_ID] as string,
      queryParams: {
        enabled: !!lineItem?.[PARK_ITEM_ID],
      },
    });
    
  const { mutate: approveInfo, isPending: isApprovePending } =
    useAddApproveDocMutation({
      onError: (error?: any) => {
        setOpenApproveRejectDialog(false);
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: (data: any) => {
        queryClient.invalidateQueries({
          queryKey: ["park-documents"],
        });
        setOpenApproveRejectDialog(false);
        if (data?.data?.message && data?.data?.message?.split(" ")[1] > 0 ) {
          onBackClick();
          toast.success(
             "Line Items Approved"
          );
        } else {
          
          const msg = data?.data?.data?.map((val: any) => {
            const idx = formLineItems?.findIndex((item: any) => item?.parkDocumentLineItemId == val?.documentLineItemId)
            return `Row No: ${idx + 1} - ${val?.message}`
          });

          toast.error(
            msg?.join(", ") || "Something went wrong",
           {
             className: "!bg-red-600 !text-white",
           }
         );
        }
      },
    });

  const { mutate: rejectInfo, isPending: isRejectPending } =
    useAddRejectDocMutation({
      onError: (error?: any) => {
        setOpenApproveRejectDialog(false);
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: (data: any) => {
        setOpenApproveRejectDialog(false);
        queryClient.invalidateQueries({
          queryKey: ["park-documents"],
        });
        if (data?.data?.message && data?.data?.message?.split(" ")[1] > 0 ) {
          onBackClick();
          toast.success(
             "Line Items Rejected"
          );
        } else {
          toast.error(
            "Something went wrong",
           {
             className: "!bg-red-600 !text-white",
           }
         );
        }
      },
    });

  const { mutate: postInfo, isPending: isPostingPending } =
    usePostDocMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setOpenPostDialog(false);
        queryClient.invalidateQueries({
          queryKey: ["park-documents"],
        });
        onBackClick();
        toast.success(`Document ${invoiceId} Posted Successfully`);
      },
    });

  const { mutate: deleteLineItemInfo, isPending: isDeletingLineItem } =
    useDeleteLineItemByIdMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setOpenDeleteLineItemDialog(false);
        toast.success("Line Item Deleted Successfully");
      },
    });

  return {
    vendorList,
    isVendorLoading,
    documentTypes,
    isDocumentTypeLoading,
    addInfo,
    isAddPending,
    updateInfo,
    isUpdatePending,
    docDetails,
    isDetailLoading,
    addSubmitForApprovalInfo,
    isAddSubmitForApprovalPending,
    isAddCommentPending,
    lineItemComment,
    setLineItemComment,
    lineItemCommentText,
    isCommentLoading,
    lineItem,
    setLineItem,
    approveInfo,
    isApprovePending,
    rejectInfo,
    isRejectPending,
    postInfo,
    isPostingPending,
    openApproveRejectDialog,
    setOpenApproveRejectDialog,
    openPostDialog,
    setOpenPostDialog,
    glAccount,
    isGLAccountLoading,
    paymentMethodOptions,
    isPaymentMethodLoading,
    paymentTermOptions,
    isPaymentTermLoading,
    deletedLineItem,
    setDeletedLineItem,
    deleteLineItemInfo,
    isDeletingLineItem,
    openDeleteLineItemDialog,
    setOpenDeleteLineItemDialog,
    isAttachmentPending,
    isDeletePending,
    attachmentInfo,
    isAttachmentLoading,
    attachment,
    isPosted,
    isEntryView,
    setIsEntryView
  };
};

export default useApi;
